// #2260: keeps a candidate on the taking screen when the AccessToken dies mid-exam.
//
// Before this, a 401 anywhere (session poll, save, submit) ran HttpClient.handleGlobalError's
// logout cascade: user store wiped (uuid → ''), router.replace(Root) — which the taking screen's
// onBeforeRouteLeave blocks. Result: a logged-out candidate stuck on the exam page, every submit
// 401, and the multi-file auto-save writing to an S3 path with an empty uuid segment.
//
// While a session is active, a 401 instead flips `expired`, snapshots the current answer
// (examAnswerDraft) and opens the session-expired popup. The user store is left intact so the
// uuid-based S3 path and the draft key keep their owner. The popup can be dismissed; submitting or
// finishing while expired re-opens it (promptRelogin) instead of sending a request. Recovery is a
// full-page login round-trip (SessionExpiredPopup), so there is no in-place "resume" state here.
// Token lifetime itself is renewed by the server (START response / session-state poll).
//
// Module-level state on purpose, same contract as useAssessmentSeat: written only from client
// lifecycle hooks / code reached by them (page, HttpClient interceptor, poll), never during SSR.

import { AxiosError } from 'axios';
import { effectScope, watch, type EffectScope } from 'vue';
import { useDialogStore } from '~/store/dialog/dialogStore';
import DialogType from '~/components/Common/Dialog/model/DialogType';
import { saveExamAnswerDraft } from '~/composables/examAnswerDraft';

// Mount count, not a flag: the taking page mounts twice and Vue can mount the second instance
// before unmounting the first (see useAssessmentSeat), so a boolean would be cleared under the survivor.
let mounted = 0;
let expired = false;
// [X] on the popup. Until then, the popup must come back whenever the single main dialog slot frees
// up — another popup (end-time warning, …) may have replaced it before the candidate ever saw it.
let dismissedByUser = false;
// The assessment ended (timer ran out) on this screen: nothing is left to log back in for, so the
// session-expired popup must never come back for the rest of this page.
let ended = false;
// Detached scope for the dialog-slot watcher: lives from the first activate() to the last deactivate(),
// not tied to whichever page instance happened to mount first.
let dialogWatchScope: EffectScope | null = null;

const isActive = (): boolean => mounted > 0;

/** (Re)opens the popup unless it is already the dialog on screen; re-arms the auto re-open. */
const promptRelogin = (): void => {
    if (ended) return;
    dismissedByUser = false;
    const dialogStore = useDialogStore();
    if (dialogStore.show && dialogStore.options.type === DialogType.SessionExpired) return;
    dialogStore.open({ type: DialogType.SessionExpired });
};

/** Idempotent: only the first detection snapshots the answer and opens the popup. */
const expire = (): void => {
    if (expired) return;
    expired = true;
    try {
        saveExamAnswerDraft();
    } catch (e) {
        // Storage unavailable/full: the [로그인] click retries and refuses to leave on failure.
        console.error(e);
    }
    promptRelogin();
};

const watchDialogSlot = (): void => {
    const dialogStore = useDialogStore();
    dialogWatchScope = effectScope(true);
    dialogWatchScope.run(() => {
        // false = slot empty. Another dialog replacing ours is left alone; once the slot is empty
        // again (that dialog closed), the session-expired popup comes back unless the user hid it.
        watch(() => dialogStore.show && dialogStore.options.type, (onScreen) => {
            if (onScreen === false && expired && !dismissedByUser) promptRelogin();
        });
    });
};

const stopWatchingDialogSlot = (): void => {
    dialogWatchScope?.stop();
    dialogWatchScope = null;
};

export const useExamSession = (): {
    isActive: () => boolean;
    isExpired: () => boolean;
    activate: () => void;
    deactivate: () => void;
    expire: () => void;
    promptRelogin: () => void;
    dismiss: () => void;
    end: () => void;
    handleUnauthorized: (e: unknown) => boolean;
    reset: () => void;
} => ({
    isActive,
    isExpired: () => expired,

    activate: (): void => {
        mounted += 1;
        if (mounted === 1) watchDialogSlot();
    },

    /**
     * Once the last instance is gone, drop the expiry too: leaving via an allowed route with the
     * popup open must not leave `expired` latched, or expire() would no-op on the next entry —
     * and the popup must not stay over the next page.
     */
    deactivate: (): void => {
        mounted = Math.max(mounted - 1, 0);
        if (mounted > 0) return;
        stopWatchingDialogSlot();
        const wasExpired = expired;
        expired = false;
        dismissedByUser = false;
        ended = false;
        if (!wasExpired) return;
        const dialogStore = useDialogStore();
        if (dialogStore.show && dialogStore.options.type === DialogType.SessionExpired) dialogStore.close();
    },

    expire,
    promptRelogin,

    /** The candidate hid the popup with [X]: keep working; only submit/finish/… re-open it. */
    dismiss: (): void => {
        dismissedByUser = true;
        useDialogStore().close();
    },

    /**
     * The assessment is over (time ran out while the session was dead): close the session-expired
     * popup and never show it again on this page — the caller shows the time-over popup instead.
     * Also counts as expired so later 401s neither snapshot a draft nor prompt a login.
     */
    end: (): void => {
        ended = true;
        expired = true;
        stopWatchingDialogSlot();
        const dialogStore = useDialogStore();
        if (dialogStore.show && dialogStore.options.type === DialogType.SessionExpired) dialogStore.close();
    },

    /**
     * True only for an AxiosError 401 while the taking screen is active: expires the session, or —
     * when it already expired and the popup was dismissed — shows the popup again, so an action
     * that failed on the dead session never fails silently.
     */
    handleUnauthorized: (e: unknown): boolean => {
        if (!isActive() || !(e instanceof AxiosError) || e.response?.status !== 401) return false;
        if (expired) promptRelogin();
        else expire();
        return true;
    },

    reset: (): void => {
        stopWatchingDialogSlot();
        mounted = 0;
        expired = false;
        dismissedByUser = false;
        ended = false;
    },
});
